import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  parseNotificationsResponse,
  getNotificationVisual,
  matchesNotificationFilter,
  NOTIF_VIEW_ALL_PAGE_SIZE,
  NOTIFICATION_PAGE_FILTER_OPTIONS,
} from '../utils/customerNotifications';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { notificationsAPI } from '../services/api';
import { toast } from 'react-toastify';

const ACCENT_COLOR = '#FF8C42';
const BORDER_COLOR = '#e0e0e0';
const TEXT_PRIMARY = '#424242';
const TEXT_SECONDARY = '#616161';

const headerCardSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  mb: 4,
  p: 4,
  borderRadius: 4,
  background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
  border: '1px solid rgba(255, 140, 66, 0.2)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
};

export default function Notifications() {
  const navigate = useNavigate();
  const { t, locale } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { token } = useAuth();
  const { unreadCount, refresh: refreshHeader } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');

  const formatDate = useCallback(
    (iso) => {
      if (!iso) return '—';
      try {
        const loc = locale === 'de' ? 'de-DE' : 'en-US';
        return new Date(iso).toLocaleString(loc, { dateStyle: 'medium', timeStyle: 'short' });
      } catch {
        return String(iso);
      }
    },
    [locale]
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await notificationsAPI.getList(1, NOTIF_VIEW_ALL_PAGE_SIZE, locale);
      const parsed = parseNotificationsResponse(res);
      setItems(parsed.items);
    } catch {
      toast.error(t('layout.notifications.loadError'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, t, locale]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      refreshHeader(true);
    },
    [refreshHeader]
  );

  const filtered = useMemo(
    () =>
      items.filter((n) =>
        matchesNotificationFilter(n?.category, categoryFilter, n?.relatedEntityType)
      ),
    [items, categoryFilter]
  );

  const handleMarkOne = async (e, notif) => {
    e?.stopPropagation?.();
    if (notif?.deepLink) {
      navigate(notif.deepLink);
      return;
    }
    if (!notif?.id || notif.isRead) return;
    setMarkingId(notif.id);
    try {
      await notificationsAPI.markRead(notif.id);
      await load();
      await refreshHeader(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.response?.data?.errorMessage || t('layout.toast.error')
      );
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAll = async () => {
    if (markingAll || unreadCount < 1) return;
    setMarkingAll(true);
    try {
      await notificationsAPI.markAllRead();
      await load();
      await refreshHeader(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.errorMessage || t('layout.toast.error'));
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, maxWidth: 1400, mx: 'auto', pr: { xs: 1, sm: 2 } }}>
      <Box sx={{
        ...headerCardSx,
        p: { xs: 2, sm: 4 },
        borderRadius: { xs: 2, sm: 4 },
      }}>
        <Box sx={{ minWidth: 0, flex: '1 1 240px', width: { xs: '100%', sm: 'auto' } }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: TEXT_PRIMARY }}>
            {t('layout.notifications.allTitle')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: TEXT_SECONDARY }}>
            {t('layout.notifications.pageSubtitle')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-start' : 'flex-end',
            gap: 2,
            flex: '1 1 auto',
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, bgcolor: '#fff' }}>
            <InputLabel id="notif-category-filter">{t('layout.notifications.filterLabel')}</InputLabel>
            <Select
              labelId="notif-category-filter"
              label={t('layout.notifications.filterLabel')}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {NOTIFICATION_PAGE_FILTER_OPTIONS.map(({ value, labelKey }) => (
                <MenuItem key={value || 'all'} value={value}>
                  {t(labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            fullWidth={isMobile}
            disabled={markingAll || unreadCount < 1}
            onClick={handleMarkAll}
            sx={{
              bgcolor: ACCENT_COLOR,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              height: '40px',
              boxShadow: 'none',
              flexShrink: 0,
              '&:hover': { bgcolor: '#08284F', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: BORDER_COLOR, color: TEXT_SECONDARY },
            }}
          >
            {markingAll ? t('layout.notifications.marking') : t('layout.notifications.markAllRead')}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 7 }}>
          <CircularProgress size={38} sx={{ color: ACCENT_COLOR }} />
          <Typography sx={{ mt: 2, color: TEXT_SECONDARY }}>{t('layout.notifications.loading')}</Typography>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${BORDER_COLOR}`,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          {filtered.length > 0 ? (
            filtered.map((notif, idx) => {
              const vis = getNotificationVisual(notif.category, notif.relatedEntityType);
              const IconComp = vis.Icon;
              const busy = markingId === notif.id;
              return (
                <Box
                  key={notif.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${BORDER_COLOR}` : 'none',
                    bgcolor: notif.isRead ? 'transparent' : 'rgba(239, 246, 255, 0.85)',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.04)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '10px',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: vis.bg,
                      color: vis.color,
                    }}
                  >
                    <IconComp sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: TEXT_PRIMARY, mb: 0.5 }}>
                      {notif.title || '—'}
                    </Typography>
                    <Typography
                      sx={{
                        color: TEXT_SECONDARY,
                        fontSize: 14,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        mb: 1,
                      }}
                    >
                      {notif.body || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeFilledIcon sx={{ fontSize: 16, color: TEXT_SECONDARY }} />
                        <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
                          {formatDate(notif.createdAtUtc)}
                        </Typography>
                      </Box>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: notif.isRead ? TEXT_SECONDARY : '#9a4a0a',
                        }}
                      >
                        {notif.isRead ? t('layout.notifications.statusRead') : t('layout.notifications.statusUnread')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                    {notif.deepLink ? (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMarkOne(e, notif)}
                        aria-label={t('layout.notifications.openLink')}
                        sx={{
                          color: ACCENT_COLOR,
                          '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.12)' },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMarkOne(e, notif)}
                        disabled={notif.isRead || busy}
                        aria-label={t('layout.notifications.markRead')}
                        sx={{
                          color: ACCENT_COLOR,
                          '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.12)' },
                          '&.Mui-disabled': { color: BORDER_COLOR },
                        }}
                      >
                        {busy ? (
                          <CircularProgress size={16} sx={{ color: ACCENT_COLOR }} />
                        ) : (
                          <MarkEmailReadIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography sx={{ px: 3, py: 6, textAlign: 'center', color: TEXT_SECONDARY }}>
              {t('layout.notifications.empty')}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
